import { fixQuillNestedBlocks } from '@/helpers/quillNestedList';
import { setCsrfToken } from './csrf';
import {
  getRequest, postRequest,
} from './request';
import { fixNoHtmlBlocks } from './helpers/fixNoHtmlBlocks';
import {
  fixUnsupportedCharacters, fixUnsupportedCharacterString,
} from './helpers/fixUnsupportedCharacters';

export const vueAuthData = async () => {
  const data = await getRequest({ url: '/vue/auth-data' });

  if (data.csrfToken) {
    setCsrfToken(data.csrfToken);
  }

  return {
    atsName: data.atsName,
    atsId: data.atsId,
    integrationTypeKey: data.integrationTypeKey,
    integrationType: data.integrationType,
    isSuperuser: data.isSuperuser,
    isLoggedIn: data.isLoggedIn,
    userType: data.userType,
    userSetupStatus: data.userSetupStatus,
    companyName: data.companyName,
    projectId: data.projectId,
    projectName: data.projectName,
    projectStatus: data.projectStatus,
    projectSetupStatus: data.projectSetupStatus,
    projectMaintenanceMode: data.projectMaintenanceMode,
    projectImageUrl: data.projectImageUrl,
    extensionJobsEnabled: data.extensionJobsEnabled,
    showIntegrationsPage: data.showIntegrationsPage,
    isTrackingEnabled: data.isTrackingEnabled,
    flashMessages: data.flashMessages,
    loginMethod: data.loginMethod,
    oauthClient: data.oauthClient,
    ssoName: data.ssoName,
    canDemographicSurveyReport: data.canDemographicSurveyReport,
    demographicSurveyReportVersion: data.demographicSurveyReportVersion,
    aiEnabled: data.aiEnabled,
    noEditorEnabled: data.noEditorEnabled,
    isRoleManagementRestricted: data.isRoleManagementRestricted,
    jobProfileEnabled: data.jobProfileEnabled,
    flsaEnabled: data.flsaEnabled,
    acknowledgementsEnabled: data.acknowledgementsEnabled,
    nextUiEnabled: data.nextUiEnabled,
    ascentEnabled: data.ascentEnabled,
    inclusionMeterDisabled: data.inclusionMeterDisabled,
    accountExecutiveEmail: data.accountExecutiveEmail,
    accountExecutiveName: data.accountExecutiveName,
    userId: data.userId,
    appMaintenanceMode: data.appMaintenanceMode,
    isApplicationReportingEnabled: data.isApplicationReportingEnabled,
    showWeeklyReports: data.showWeeklyReports,
    firstName: data.firstName,
    lastName: data.lastName,
    role: data.role,
    email: data.email,
    appVersion: data.appVersion,
    personaKey: data.personaKey,
    hasInstalledDA: data.hasInstalledDA,
    ipAddress: data.ipAddress,
    jobProfileLevels: data.jobProfileLevels ?? [],
    rbac: data.rbac || {
      roleNames: [],
      permissions: {},
    },
  };
};

const getDocumentFields = (data) => ({
  id: data.id,
  jobId: data.job_id,
  documentType: data.document_type,
  detached: data.detached,
  title: fixUnsupportedCharacterString(data.title),
  company: data.company,
  location: data.location,
  reqReportUrl: data.reqReportUrl,
  description: data.description,
  score: data.score,
  bodyMessages: data.bodyMessages,
  tags: data.tags,
  specificityKey: data.specificityKey,
  specificityData: data.specificityData,
  scales: data.scales,
  jobStatus: data.jobStatus,
  scoreImpact: data.scoreImpact,
  ranking: data.ranking,
  isPrivate: data.isPrivate,
  isFlagship: data.is_flagship,
  wordCount: data.wordCount,
  // TODO: fix camel-case
  ats_requisition_id: data.ats_requisition_id,
  flexible_id: data.flexible_id || '',
  type_key: data.type_key,
  description_delta: data.description_delta,
  description_json: fixUnsupportedCharacters(
    fixQuillNestedBlocks(
      fixNoHtmlBlocks(data.description_json),
    ),
  ),
  isSyncable: data.is_syncable,
  isPermissionTogglable: data.is_permission_togglable,
  isHidden: data.is_hidden,
  postSummary: data.postSummary || {
    nonCompliantJobs: 0,
    employerBrandJobs: 0,
    jobPerformanceJobs: 0,
  },
});

export const vueGetPreview = async ({ id, revision }) => {
  const body = {};

  if (typeof id !== 'undefined') body.id = id;
  if (revision && typeof revision !== 'undefined') body.revision = revision;
  if (window.sessionStorage.shareJobId === id) {
    const { shareKey } = window.sessionStorage;
    if (shareKey) body.key = shareKey;
  }

  const data = await postRequest({ url: '/vue/get-preview', body });
  return {
    ...getDocumentFields(data),
    success: data.success,
    allRevisions: data.allRevisions,
  };
};

export const vueGetTemplatePreview = async ({ jobId, templateId }) => {
  const body = { jobId, templateId };

  if (window.sessionStorage.shareJobId === jobId) {
    const { shareKey } = window.sessionStorage;
    if (shareKey) body.key = shareKey;
  }

  const data = await postRequest({ url: '/vue/get-template-preview', body });
  return {
    template: data.template,
  };
};

export const vueGet = async ({ id, revision }) => {
  const body = {};

  if (typeof id !== 'undefined') body.id = id;
  if (revision && typeof revision !== 'undefined') body.revision = revision;
  if (window.sessionStorage.shareJobId === id) {
    const { shareKey } = window.sessionStorage;
    if (shareKey) body.key = shareKey;
  }

  const data = await postRequest({ url: '/vue/get', body });
  return {
    ...getDocumentFields(data),
    success: data.success,
    allRevisions: data.allRevisions,
  };
};

export const vueShareLink = async ({
  jobId,
  firstName,
  lastName,
  email,
  emailExisting,
  sendEmail,
}) => {
  const data = await postRequest({
    url: '/vue/share-link',
    body: {
      id: jobId,
      firstname: firstName,
      lastname: lastName,
      email,
      sendemail: sendEmail,
      'email-existing': emailExisting,
    },
  });

  return { type: data.type, url: data.url, users: data.users };
};

export const vueNotify = async ({
  jobId,
  userId,
  notificationType,
}) => postRequest({
  url: '/vue/notify',
  body: { job_id: jobId, user_id: userId, notification_type: notificationType },
});

export const vueOnboard = async ({
  userId,
}) => postRequest({
  url: '/vue/onboard',
  body: { user: userId },
});

export const vueSyncWithAts = async ({ jobId, integrationTypeKey }) => {
  const data = await postRequest({
    url: `/vue/sync-with-ats?id=${jobId}&integration_type_key=${integrationTypeKey}`,
  });
  return { status: data.status };
};

export const vueRescore = async ({
  id,
  jobId,
  detached,
  isExtension,
  hash,
  key,
  title,
  company,
  location,
  description,
  descriptionDelta,
  descriptionJson,
  flexibleId,
}) => {
  const fields = descriptionJson?.fields || [];
  const data = await postRequest({
    url: '/vue/rescore',
    body: {
      id,
      jobId,
      detached,
      is_extension: isExtension,
      key,
      hash,
      title,
      location,
      company,
      flexible_id: flexibleId,
      description,
      description_delta: descriptionDelta,
      description_json: JSON.stringify({ fields }),
    },
  });

  return {
    ...getDocumentFields(data),
    hash: data.hash,
    allRevisions: data.allRevisions,
  };
};

export const getAtsParams = async (atsId) => {
  const { fields, templateList } = await getRequest({
    url: `/vue/get-ats-params?ats_id=${atsId}`,
  });

  return { fields, templateList };
};

export const vueCordToken = async ({ jobId } = {}) => {
  const body = {};

  if (jobId && Number(window.sessionStorage.shareJobId) === Number(jobId)) {
    body.shareKey = window.sessionStorage.shareKey;
    body.jobId = jobId;
  }

  return postRequest({ url: '/vue/cord-token', body });
};
